import TextField from 'Shared/components/TextField'
import useFlowTranslate from 'FlowBuilder/hooks/useFlowTranslate'
import { flowTranslatableTemplates, flowTranslatablesBase, validationTranslatables } from 'FlowBuilder/lib/flowTranslatable'
import { useLanguagesData } from 'FlowBuilder/hooks/useLanguageFlow'
import './TranslationSettings.scoped.scss'
import { Translatable } from 'Shared/TranslatableTextContent/translatable'
import TranslatableHtmlField from './TranslatableHtmlField'
import { useAct } from 'FlowBuilder/store'

export default function FlowTranslationSettings() {
  const flowTemplateFields = useSel(s => s.builder?.flow?.template?.fields || {})

  const flowTemplateFieldsTranslatableConfig = Object.keys(flowTemplateFields).reduce((acc, key) => {
    const field = flowTemplateFields[key]
    if (field.translatable) {
      acc[_.camelCase(field.slug)] = {
        label: field.label,
        default: field.defaultValue,
        html: !!field.wysiwyg,
      }
    }
    return acc
  }, {} as Record<string, Translatable>)

  const flowGeneral = { ...flowTranslatablesBase, ...flowTranslatableTemplates, ...flowTemplateFieldsTranslatableConfig }

  return (
    <div>
      <h3>Page</h3>
      <TranslateOptions>
      {Object.keys(flowGeneral).map((strKey) => (
        <FlowTranslateSetting strKey={strKey} translatable={flowGeneral[strKey]} key={strKey} />
      ))}
      </TranslateOptions>
      <h3>Validation</h3>
      <p>These validation messages are shown when users input invalid data.</p>
      <TranslateOptions>
      {Object.keys(validationTranslatables).map((strKey) => (
        <FlowTranslateSetting strKey={strKey} translatable={validationTranslatables[strKey]} key={strKey} />
      ))}
      </TranslateOptions>
    </div>
  )
}

function TranslateOptions({children}) {
  const { editableLanguagesData } = useLanguagesData()

  return (
    <div
      className="translatable"
      style={{
        gridTemplateColumns: `repeat(${Object.keys(editableLanguagesData).length + 1}, 1fr)`,
      }}
    >
      {children}
    </div>
  )
}

function FlowTranslateSetting({ strKey, translatable }) {
  const {
    fallback,
  } = useFlowTranslate()
  const { editableLanguagesData } = useLanguagesData()

  if (translatable.hideIfNotPresent && !fallback[strKey]) {
    return null
  }

  return <>
    <div className="translatable-label">
      <span><strong>{translatable.label}</strong></span>
    </div>
    {Object.keys(editableLanguagesData).map((lang) => (
      <FlowTranslateLanguageSetting
        key={lang}
        strKey={strKey}
        translatable={translatable}
        languageOverride={lang}
      />
    ))}
  </>
}

function FlowTranslateLanguageSetting({ strKey, translatable, languageOverride }) {
  const act = useAct()
  const {
    translated,
    fallback,
    textContentUpdater,
    flag,
  } = useFlowTranslate({ languageOverride: languageOverride })

  function openHtmlEditor() {
    act.local.setModal({
      kind: 'editContent',
      fieldKind: 'flowTextContent',
      slug: strKey,
      wysiwyg: true,
      language: languageOverride,
    })
  }

  return <div className={`translatable-input translatable-input-${languageOverride} translatable-input-${strKey}`}>
    <span>{flag}</span>
    {translatable.html ? (
      <TranslatableHtmlField
        value={translated[strKey] || ''}
        placeholder={fallback[strKey] || translatable.default || ''}
        onClick={openHtmlEditor}
      />
    ) : (
      <TextField
        key={strKey}
        value={translated[strKey] || ''}
        placeholder={fallback[strKey] || translatable.default || ''}
        onChange={(val) => textContentUpdater(strKey, val)}
      />
    )}
  </div>
}
